import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from './config/api';

import { 
  conversationAPI, 
  messageAPI, 
  apiErrorUtils
} from './apiService';
import { 
  ErrorNotification, 
  ErrorBoundary 
} from './ErrorNotification';
import { 
  getRecoveryStrategy,
  RECOVERY_STRATEGIES 
} from './errorHandling';
import { serviceHealth } from './serviceHealth';
import { 
  trackNewConversation, 
  trackDeletedChat, 
  trackSidebarOpen, 
  trackSidebarClose, 
  trackNewMessage 
} from './utils/analytics';
import { trackUserAction, trackError } from './utils/newrelic';
import AddToHomeScreenPrompt from './components/AddToHomeScreenPrompt';

// Feature flag to show original model names
const SHOW_ORIGINAL_MODEL_NAMES = true;

function ChatApp() {
  const { isAuthenticated, currentUser, logout } = useAuth();
  const [conversations, setConversations] = useState({});
  const [currentConversationId, setCurrentConversationId] = useState(null);

  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [conversationError, setConversationError] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagePages, setMessagePages] = useState({}); // conversationId -> { messages: [], hasMore: boolean, oldestTimestamp: number }
  const [optimisticMessages, setOptimisticMessages] = useState({}); // conversationId -> messages[]
  const [operationStatus, setOperationStatus] = useState({}); // Track status of operations
  const [retryQueue, setRetryQueue] = useState([]); // Queue of failed operations to retry
  const [currentError, setCurrentError] = useState(null); // Current error for notification
  const [isOfflineMode, setIsOfflineMode] = useState(false); // Offline mode state
  const [fallbackDataAge, setFallbackDataAge] = useState(null); // Age of fallback data

  const [inputMessage, setInputMessage] = useState('');
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-4-5');
  const [defaultModel, setDefaultModel] = useState('claude-sonnet-4-5');
  const [models, setModels] = useState([]);
  const [recommendedModel, setRecommendedModel] = useState('claude-sonnet-4-5');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Helper function to process messages and detect model change messages
  const processMessages = (messages) => {
    return (messages || []).map(message => {
      // Detect model change messages by content pattern
      if (message.role === 'system' && 
          (message.content.includes('Model changed from') || 
           (message.content.includes('Using ') && message.content.includes('change in the top right')))) {
        return { ...message, isModelChange: true };
      }
      return message;
    });
  };

  // Get current conversation messages with pagination and optimistic updates
  const messages = currentConversationId ? [
    ...(messagePages[currentConversationId]?.messages || conversations[currentConversationId]?.messages || []),
    ...(optimisticMessages[currentConversationId] || [])
  ] : [];

  useEffect(() => {
    if (isAuthenticated) {
      fetchModels();
      loadDefaultModel();
    }
  }, [isAuthenticated]);

  // Validate models when they change
  useEffect(() => {
    if (models.length > 0) {
      const validatedSelectedModel = validateAndSwitchModel(selectedModel, models, recommendedModel);
      if (validatedSelectedModel !== selectedModel) {
        setSelectedModel(validatedSelectedModel);
        setDefaultModel(validatedSelectedModel);
        saveDefaultModel(validatedSelectedModel);
      }
    }
  }, [models, recommendedModel]);

  // Load conversations when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🔐 Authenticated, loading conversations...');
      loadConversations();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close model dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelDropdownOpen && !event.target.closest('.model-selector-container')) {
        setModelDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [modelDropdownOpen]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Auto-close sidebar when switching from mobile to desktop
      if (!mobile && sidebarOpen) {
        setSidebarOpen(true); // Keep it open on desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  // Handle mobile keyboard appearance
  useEffect(() => {
    if (!isMobile) return;

    const handleVisualViewportResize = () => {
      if (textareaRef.current && document.activeElement === textareaRef.current) {
        // Scroll the textarea into view when keyboard appears
        setTimeout(() => {
          textareaRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }, 100);
      }
    };

    // Use visualViewport API if available (better for keyboard detection)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
      return () => {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      };
    }
  }, [isMobile]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const validateAndSwitchModel = (currentModel, availableModels, recommended) => {
    // Don't validate if models list is empty
    if (!availableModels || availableModels.length === 0) {
      console.log(`⚠️ Model validation skipped - no models loaded yet. Current model: ${currentModel}`);
      return currentModel;
    }
    
    const modelExists = availableModels.some(model => model.id === currentModel);
    console.log(`🔍 Validating model "${currentModel}" - exists: ${modelExists}, available models: ${availableModels.map(m => m.id).join(', ')}`);
    
    if (!modelExists && currentModel !== recommended) {
      const recommendedModelName = availableModels.find(model => model.id === recommended)?.name || recommended;
      console.log(`❌ Model "${currentModel}" not found, switching to recommended: ${recommended}`);
      alert(`The model "${currentModel}" you were using is no longer available. This has been switched to the recommended ${recommendedModelName}.`);
      return recommended;
    }
    return modelExists ? currentModel : recommended;
  };

  const fetchModels = async () => {
    try {
      const token = getCookie('auth_token');
      const response = await axios.get(`${API_BASE_URL}/models`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const fetchedModels = response.data.models;
      const recommended = response.data.recommendedModel || 'claude-sonnet-4-5';
      
      setModels(fetchedModels);
      setRecommendedModel(recommended);
      
      // Validate current selected model
      const validatedSelectedModel = validateAndSwitchModel(selectedModel, fetchedModels, recommended);
      if (validatedSelectedModel !== selectedModel) {
        setSelectedModel(validatedSelectedModel);
        setDefaultModel(validatedSelectedModel);
        saveDefaultModel(validatedSelectedModel);
      }
      
    } catch (error) {
      console.error('Error fetching models:', error);
      if (error.response?.status === 401) {
        logout();
        return;
      }
      // Fallback models if API fails (ordered by cost/value balance)
      const fallbackModels = [
        // Best Value - Top performance at reasonable cost (Recommended)
        { 
          id: 'claude-3-5-sonnet', 
          name: '🧠 Claude 3.5 Sonnet - Best Overall (Recommended)',
          originalName: 'Claude 3.5 Sonnet',
          emoji: '🧠',
          description: 'Best Overall (Recommended)',
          useCase: 'Complex research, thesis work, advanced problem-solving, detailed analysis. The go-to for serious academic work.',
          ratings: { speed: 3, reasoning: 5, accuracy: 5, cost: 4 }
        },
        { 
          id: 'claude-sonnet-4', 
          name: '🎓 Claude Sonnet 4 - Next Generation',
          originalName: 'Claude Sonnet 4',
          emoji: '🎓',
          description: 'Next Generation',
          useCase: 'Cutting-edge reasoning, complex tasks, research excellence. Latest Sonnet generation with breakthrough capabilities.',
          ratings: { speed: 3, reasoning: 5, accuracy: 5, cost: 4 }
        },
        { 
          id: 'claude-3-7-sonnet', 
          name: '🧠 Claude 3.7 Sonnet - Enhanced Reasoning',
          originalName: 'Claude 3.7 Sonnet',
          emoji: '🧠',
          description: 'Enhanced Reasoning',
          useCase: 'Advanced analysis, complex problem-solving, detailed research. Improved reasoning capabilities over 3.5.',
          ratings: { speed: 3, reasoning: 5, accuracy: 5, cost: 4 }
        },
        { 
          id: 'gpt-oss-120b', 
          name: '🚀 GPT OSS 120B - Large Open Source',
          originalName: 'GPT OSS 120B',
          emoji: '🚀',
          description: 'Large Open Source',
          useCase: 'Complex reasoning, advanced analysis, high-quality responses. Cheaper than Claude 4 with excellent performance.',
          ratings: { speed: 2, reasoning: 5, accuracy: 5, cost: 3 }
        },
        { 
          id: 'mistral-large-2402', 
          name: '🎯 Mistral Large (24.02) - Premium European AI',
          originalName: 'Mistral Large (24.02)',
          emoji: '🎯',
          description: 'Premium European AI',
          useCase: 'Advanced reasoning, multilingual tasks, complex analysis. European alternative to GPT-4 class models.',
          ratings: { speed: 3, reasoning: 5, accuracy: 5, cost: 3 }
        },
        // Premium - Latest & most capable (higher cost)
        { 
          id: 'claude-sonnet-4-5', 
          name: '🎓 Claude Sonnet 4.5 - State of the Art',
          originalName: 'Claude Sonnet 4.5',
          emoji: '🎓',
          description: 'State of the Art',
          useCase: 'Most advanced reasoning, complex research, expert-level analysis. Top-tier performance for demanding tasks.',
          ratings: { speed: 3, reasoning: 5, accuracy: 5, cost: 4 }
        },
        { 
          id: 'claude-opus-4-5', 
          name: '👑 Claude Opus 4.5 - Ultimate Intelligence',
          originalName: 'Claude Opus 4.5',
          emoji: '👑',
          description: 'Ultimate Intelligence',
          useCase: 'Maximum reasoning power, creative excellence, nuanced analysis. The most capable Claude model for the most demanding work.',
          ratings: { speed: 2, reasoning: 5, accuracy: 5, cost: 5 }
        },
        { 
          id: 'claude-opus-4-7', 
          name: '👑 Claude Opus 4.7 - Most Capable',
          originalName: 'Claude Opus 4.7',
          emoji: '👑',
          description: 'Most Capable',
          useCase: 'Anthropic\'s most capable model. Excels at agentic coding, long-horizon tasks, complex reasoning, and enterprise workflows. 1M token context window.',
          ratings: { speed: 2, reasoning: 5, accuracy: 5, cost: 5 }
        },
        // Fast & Powerful - Great speed/quality balance
        { 
          id: 'claude-haiku-4-5', 
          name: '🏃 Claude Haiku 4.5 - Ultra-Fast Intelligence',
          originalName: 'Claude Haiku 4.5',
          emoji: '🏃',
          description: 'Ultra-Fast Intelligence',
          useCase: 'Lightning-fast responses, code review, quick analysis, real-time assistance. Latest Haiku with improved speed and accuracy.',
          ratings: { speed: 5, reasoning: 4, accuracy: 5, cost: 2 }
        },
        { 
          id: 'llama-3-1-70b', 
          name: '🔓 Llama 3.1 70B - Powerful Open Source',
          originalName: 'Meta Llama 3.1 70B Instruct',
          emoji: '🔓',
          description: 'Powerful Open Source',
          useCase: 'Advanced coding, technical research, complex reasoning. Open-source alternative for sophisticated tasks.',
          ratings: { speed: 3, reasoning: 5, accuracy: 4, cost: 2 }
        },
        { 
          id: 'nova-pro', 
          name: '🎯 Nova Pro - Balanced Speed & Quality',
          originalName: 'Amazon Nova Pro',
          emoji: '🎯',
          description: 'Balanced Speed & Quality',
          useCase: 'General academic work, essay writing, research summaries, balanced performance for most tasks.',
          ratings: { speed: 4, reasoning: 4, accuracy: 4, cost: 2 }
        },
        { 
          id: 'gpt-oss-20b', 
          name: '⚡ GPT OSS 20B - Balanced Open Model',
          originalName: 'GPT OSS 20B',
          emoji: '⚡',
          description: 'Balanced Open Model',
          useCase: 'General tasks, coding assistance, research. Good balance of performance and efficiency.',
          ratings: { speed: 4, reasoning: 4, accuracy: 4, cost: 2 }
        },
        { 
          id: 'mixtral-8x7b-instruct', 
          name: '🔀 Mixtral 8x7B - Mixture of Experts',
          originalName: 'Mixtral 8x7B Instruct',
          emoji: '🔀',
          description: 'Mixture of Experts',
          useCase: 'Specialized tasks, coding, technical writing. Efficient mixture-of-experts architecture.',
          ratings: { speed: 3, reasoning: 4, accuracy: 4, cost: 2 }
        },
        // Specialized - Coding & specific tasks
        { 
          id: 'qwen3-coder-30b', 
          name: '💻 Qwen3-Coder-30B - Specialized Coding AI',
          originalName: 'Qwen3-Coder-30B-A3B-Instruct',
          emoji: '💻',
          description: 'Specialized Coding AI',
          useCase: 'Code generation, debugging, technical documentation. Specialized for programming tasks.',
          ratings: { speed: 3, reasoning: 4, accuracy: 5, cost: 2 }
        },
        { 
          id: 'qwen3-32b', 
          name: '🐉 Qwen3 32B - Chinese AI Excellence',
          originalName: 'Qwen3 32B (dense)',
          emoji: '🐉',
          description: 'Chinese AI Excellence',
          useCase: 'Multilingual tasks, cultural understanding. Excellent for diverse perspectives and languages.',
          ratings: { speed: 3, reasoning: 4, accuracy: 4, cost: 2 }
        },
        // Budget-Friendly - Fast & cheap
        { 
          id: 'nova-2-lite', 
          name: '⚡ Nova 2 Lite - Next-Gen Speed',
          originalName: 'Amazon Nova 2 Lite',
          emoji: '⚡',
          description: 'Next-Gen Speed',
          useCase: 'Ultra-fast responses, quick tasks, simple queries. Latest generation Nova with improved efficiency.',
          ratings: { speed: 5, reasoning: 3, accuracy: 4, cost: 1 }
        },
        { 
          id: 'nova-lite', 
          name: '⚡ Nova Lite - Fastest & Cheapest',
          originalName: 'Amazon Nova Lite',
          emoji: '⚡',
          description: 'Fastest & Cheapest',
          useCase: 'Quick questions, simple tasks, brainstorming, casual conversations. Perfect for rapid-fire Q&A sessions.',
          ratings: { speed: 5, reasoning: 3, accuracy: 3, cost: 1 }
        },
        { 
          id: 'mistral-7b-instruct', 
          name: '🏃 Mistral 7B - Fast & Efficient',
          originalName: 'Mistral 7B Instruct',
          emoji: '🏃',
          description: 'Fast & Efficient',
          useCase: 'Quick responses, basic coding, simple explanations. Efficient European model for everyday tasks.',
          ratings: { speed: 5, reasoning: 3, accuracy: 4, cost: 1 }
        },
        { 
          id: 'llama-3-1-8b', 
          name: '💰 Llama 3.1 8B - Fast Open Source',
          originalName: 'Meta Llama 3.1 8B Instruct',
          emoji: '💰',
          description: 'Fast Open Source',
          useCase: 'Budget-friendly coding help, basic explanations, simple tasks. Good performance at minimal cost.',
          ratings: { speed: 4, reasoning: 3, accuracy: 3, cost: 1 }
        }
      ];
      setModels(fallbackModels);
      
      // Validate with fallback models too
      const validatedSelectedModel = validateAndSwitchModel(selectedModel, fallbackModels, recommendedModel);
      if (validatedSelectedModel !== selectedModel) {
        setSelectedModel(validatedSelectedModel);
        setDefaultModel(validatedSelectedModel);
        saveDefaultModel(validatedSelectedModel);
      }
    }
  };

  const loadDefaultModel = () => {
    // Default model will be managed server-side in the future
    // For now, keep the default model in component state only
  };

  const saveDefaultModel = (modelId) => {
    // Default model will be managed server-side in the future
    setDefaultModel(modelId);
  };

  const handleModelChange = (newModelId) => {
    const oldModel = selectedModel;
    setSelectedModel(newModelId);
    saveDefaultModel(newModelId);
    
    // Track model change in NewRelic
    trackUserAction('model_changed', {
      oldModel,
      newModel: newModelId,
      conversationId: currentConversationId
    });

    // Add model change notification to current conversation
    if (currentConversationId && oldModel !== newModelId) {
      const oldModelName = models.find(m => m.id === oldModel)?.originalName || oldModel;
      const newModelName = models.find(m => m.id === newModelId)?.originalName || newModelId;
      
      const modelChangeMessage = {
        role: 'system',
        content: `Model changed from ${oldModelName} to ${newModelName}`,
        isModelChange: true,
        timestamp: new Date().toISOString()
      };

      // Save the system message to the backend
      messageAPI.addMessage(
        currentConversationId,
        'system',
        `Model changed from ${oldModelName} to ${newModelName}`,
        newModelId
      ).catch(error => {
        console.error('Error saving model change message to backend:', error);
        // Continue with local update even if backend fails
      });

      const updatedConversations = {
        ...conversations,
        [currentConversationId]: {
          ...conversations[currentConversationId],
          messages: [...(conversations[currentConversationId]?.messages || []), modelChangeMessage],
          model: newModelId,
          updatedAt: new Date().toISOString()
        }
      };

      setConversations(updatedConversations);
      saveConversations(updatedConversations);

      // Also add the system message to messagePages so it shows up immediately
      setMessagePages(prev => ({
        ...prev,
        [currentConversationId]: {
          ...prev[currentConversationId],
          messages: [...(prev[currentConversationId]?.messages || []), modelChangeMessage]
        }
      }));
    }
  };

  // Cookie helpers
  const getCookie = (name) => {
    try {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    } catch (error) {
      console.error('Error getting cookie:', error);
      return null;
    }
  };

  // Enhanced error handling functions
  const handleApiError = (error, context = '') => {
    const classified = apiErrorUtils.handleAuthError(error, logout);
    console.error(`API Error in ${context}:`, classified);
    return classified;
  };

  const retryCurrentError = async () => {
    if (currentError) {
      setCurrentError(null);
      setConversationError(null);
      await loadConversations();
    }
  };

  const enableOfflineMode = () => {
    setIsOfflineMode(true);
    setCurrentError({
      message: 'Offline mode enabled. Changes will sync when connection is restored.',
      isFromFallback: true
    });
  };

  const resetCircuitBreakers = async () => {
    setCurrentError(null);
    setConversationError(null);
    await loadConversations();
  };

  const clearFallbackData = () => {
    // No longer using localStorage fallback
    setFallbackDataAge(null);
    setCurrentError(null);
  };

  // ... (rest of the chat functionality methods would continue here)
  // For brevity, I'll include just the essential structure and the render method

  // Enhanced Model Selector Component
  const ModelSelector = () => {
    const currentModel = models.find(model => model.id === selectedModel);
    
    const RatingBars = ({ rating, isSelected }) => (
      <div className="rating-bars">
        {[1, 2, 3, 4, 5].map(i => (
          <div 
            key={i} 
            className={`rating-bar ${i <= rating ? 'active' : ''}`}
          />
        ))}
      </div>
    );

    return (
      <div className="model-selector-container">
        <div 
          className={`model-selector-button ${modelDropdownOpen ? 'open' : ''}`}
          onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
        >
          <span className="model-emoji">{currentModel?.emoji || '🤖'}</span>
          <span>{currentModel?.description || 'Select Model'}</span>
          <span className={`dropdown-arrow ${modelDropdownOpen ? 'open' : ''}`}>▼</span>
        </div>
        
        {modelDropdownOpen && (
          <div className="model-selector-dropdown">
            {models.map(model => (
              <div
                key={model.id}
                className={`model-option ${model.id === selectedModel ? 'selected' : ''}`}
                onClick={() => {
                  handleModelChange(model.id);
                  setModelDropdownOpen(false);
                }}
              >
                <div className="model-option-header">
                  <span className="model-emoji">{model.emoji}</span>
                  <div className="model-name-container">
                    {SHOW_ORIGINAL_MODEL_NAMES && (
                      <div className="model-original-name">{model.originalName || model.id}</div>
                    )}
                    <span className="model-name">{model.description}</span>
                  </div>
                </div>
                {model.useCase && (
                  <div className="model-use-case">
                    {model.useCase}
                  </div>
                )}
                <div className="model-ratings">
                  <div className="rating-item">
                    <span className="rating-label">Speed</span>
                    <RatingBars rating={model.ratings?.speed || 3} isSelected={model.id === selectedModel} />
                  </div>
                  <div className="rating-item">
                    <span className="rating-label">Reasoning</span>
                    <RatingBars rating={model.ratings?.reasoning || 3} isSelected={model.id === selectedModel} />
                  </div>
                  <div className="rating-item">
                    <span className="rating-label">Accuracy</span>
                    <RatingBars rating={model.ratings?.accuracy || 3} isSelected={model.id === selectedModel} />
                  </div>
                  <div className="rating-item">
                    <span className="rating-label">Cost</span>
                    <RatingBars rating={model.ratings?.cost || 3} isSelected={model.id === selectedModel} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Cookie helpers
  const deleteCookie = (name) => {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } catch (error) {
      console.error('Error deleting cookie:', error);
    }
  };

  // Conversation management functions
  const generateConversationId = () => {
    return 'conv_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  };

  // Send message functionality
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    // Track user message
    trackNewMessage();
    
    // Track in NewRelic for session replay context
    trackUserAction('message_sent', {
      model: selectedModel,
      messageLength: inputMessage.trim().length,
      conversationId: currentConversationId || 'new'
    });

    const messageText = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    let conversationId = currentConversationId;

    // Create new conversation if none exists
    if (!conversationId) {
      try {
        const newConversation = await conversationAPI.createConversation('New Chat');
        conversationId = newConversation.conversationId;
        
        // Get current model name for the initial message
        const currentModelName = models.find(m => m.id === selectedModel)?.originalName || selectedModel;
        
        // Add initial system message to the conversation
        await messageAPI.addMessage(
          conversationId,
          'system',
          `Using ${currentModelName}, change in the top right`,
          selectedModel
        );
        
        // Create local conversation object
        const localConversation = {
          id: conversationId,
          title: newConversation.title,
          messages: [{
            role: 'system',
            content: `Using ${currentModelName}, change in the top right`,
            isModelChange: true,
            timestamp: new Date().toISOString()
          }],
          createdAt: newConversation.createdAt,
          updatedAt: newConversation.updatedAt,
          model: selectedModel
        };

        const updatedConversations = {
          ...conversations,
          [conversationId]: localConversation
        };

        setConversations(updatedConversations);
        setCurrentConversationId(conversationId);
        saveConversations(updatedConversations);
      } catch (error) {
        console.error('Error creating conversation for message:', error);
        // Fallback to local creation
        conversationId = generateConversationId();
        const currentModelName = models.find(m => m.id === selectedModel)?.originalName || selectedModel;
        
        const initialMessage = {
          role: 'system',
          content: `Using ${currentModelName}, change in the top right`,
          isModelChange: true,
          timestamp: new Date().toISOString()
        };
        
        const newConversation = {
          id: conversationId,
          title: 'New Chat',
          messages: [initialMessage],
          createdAt: new Date().toISOString(),
          model: selectedModel
        };

        const updatedConversations = {
          ...conversations,
          [conversationId]: newConversation
        };

        setConversations(updatedConversations);
        setCurrentConversationId(conversationId);
        saveConversations(updatedConversations);
      }
    }

    // Create user message
    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: Date.now(),
      isOptimistic: true
    };

    // Add optimistic user message
    setOptimisticMessages(prev => ({
      ...prev,
      [conversationId]: [userMessage]
    }));

    try {
      console.log('🚀 Sending message to conversation:', conversationId);
      console.log('📝 Message content:', messageText);
      console.log('🤖 Using model:', selectedModel);

      const token = getCookie('auth_token');
      const response = await axios.post(`${API_BASE_URL}/chat`, {
        message: messageText,
        conversationId: conversationId,
        model: selectedModel
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Received response:', response.data);

      // Remove optimistic message
      setOptimisticMessages(prev => ({
        ...prev,
        [conversationId]: []
      }));

      if (response.data && response.data.response) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.response,
          timestamp: Date.now(),
          isTruncated: response.data.isTruncated || false
        };

        // Update message pages instead of conversations
        setMessagePages(prev => ({
          ...prev,
          [conversationId]: {
            ...prev[conversationId],
            messages: [
              ...(prev[conversationId]?.messages || conversations[conversationId]?.messages || []),
              { ...userMessage, isOptimistic: false },
              assistantMessage
            ]
          }
        }));

        // Update conversation metadata
        const updatedConversations = {
          ...conversations,
          [conversationId]: {
            ...conversations[conversationId],
            updatedAt: new Date().toISOString(),
            model: selectedModel
          }
        };

        setConversations(updatedConversations);
        saveConversations(updatedConversations);

        // Handle new conversation title generation
        if (response.data.isNewConversation) {
          // Small delay to allow backend title generation to complete
          setTimeout(() => {
            loadConversations();
          }, 1000);
        }
      } else {
        console.error('❌ Invalid response format:', response.data);
        
        // Remove optimistic user message and add error
        setOptimisticMessages(prev => ({
          ...prev,
          [conversationId]: []
        }));

        const errorMessage = {
          role: 'assistant',
          content: 'Received invalid response format from server.',
          isError: true,
          timestamp: Date.now()
        };

        setMessagePages(prev => ({
          ...prev,
          [conversationId]: {
            ...prev[conversationId],
            messages: [
              ...(prev[conversationId]?.messages || []),
              { ...userMessage, isOptimistic: false },
              errorMessage
            ]
          }
        }));
      }

    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });

      // Track error in NewRelic
      trackError(error, {
        action: 'send_message',
        model: selectedModel,
        conversationId,
        messageLength: messageText.length,
        statusCode: error.response?.status
      });

      if (error.response?.status === 401) {
        console.log('🚪 Unauthorized, logging out');
        logout();
        return;
      }

      // Remove optimistic user message and add error
      setOptimisticMessages(prev => ({
        ...prev,
        [conversationId]: []
      }));

      const errorMessage = {
        role: 'assistant',
        content: `Error: ${error.response?.data?.error || error.message || 'Unknown error occurred'}`,
        isError: true,
        timestamp: Date.now(),
        adminDetails: error.response?.data?.adminDetails || null
      };

      setMessagePages(prev => ({
        ...prev,
        [conversationId]: {
          ...prev[conversationId],
          messages: [
            ...(prev[conversationId]?.messages || []),
            { ...userMessage, isOptimistic: false },
            errorMessage
          ]
        }
      }));
    } finally {
      console.log('🏁 Request completed, setting loading to false');
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // On iOS, allow Enter to create new lines (user must tap send button)
    // On desktop, Enter sends message (Shift+Enter for new line)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (e.key === 'Enter' && !e.shiftKey && !isIOS) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Continue a truncated response
  const continueResponse = async (conversationId) => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      console.log('🔄 Continuing truncated response for conversation:', conversationId);

      const token = getCookie('auth_token');
      const response = await axios.post(`${API_BASE_URL}/chat/continue`, {
        conversationId: conversationId,
        model: selectedModel
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Received continuation response:', response.data);

      if (response.data && response.data.response) {
        const continuationMessage = {
          role: 'assistant',
          content: response.data.response,
          timestamp: Date.now(),
          isTruncated: response.data.isTruncated || false,
          isContinuation: true
        };

        // Add continuation message to the conversation
        setMessagePages(prev => ({
          ...prev,
          [conversationId]: {
            ...prev[conversationId],
            messages: [
              ...(prev[conversationId]?.messages || []),
              continuationMessage
            ]
          }
        }));

        // Update conversation metadata
        const updatedConversations = {
          ...conversations,
          [conversationId]: {
            ...conversations[conversationId],
            updatedAt: new Date().toISOString(),
            model: selectedModel
          }
        };

        setConversations(updatedConversations);
        saveConversations(updatedConversations);
      }

    } catch (error) {
      console.error('❌ Error continuing response:', error);

      // Track error in NewRelic
      trackError(error, {
        action: 'continue_response',
        model: selectedModel,
        conversationId,
        statusCode: error.response?.status
      });

      if (error.response?.status === 401) {
        console.log('🚪 Unauthorized, logging out');
        logout();
        return;
      }

      const errorMessage = {
        role: 'assistant',
        content: `Error continuing response: ${error.response?.data?.error || error.message || 'Unknown error occurred'}`,
        isError: true,
        timestamp: Date.now()
      };

      setMessagePages(prev => ({
        ...prev,
        [conversationId]: {
          ...prev[conversationId],
          messages: [
            ...(prev[conversationId]?.messages || []),
            errorMessage
          ]
        }
      }));
    } finally {
      console.log('🏁 Continue request completed, setting loading to false');
      setIsLoading(false);
    }
  };

  // Retry failed operations
  const retryOperation = async (operation) => {
    try {
      setOperationStatus(prev => ({
        ...prev,
        [operation.id]: { status: 'retrying', error: null }
      }));

      await operation.fn();
      
      setOperationStatus(prev => ({
        ...prev,
        [operation.id]: { status: 'success', error: null }
      }));

      // Remove from retry queue
      setRetryQueue(prev => prev.filter(op => op.id !== operation.id));
    } catch (error) {
      console.error(`Retry failed for operation ${operation.id}:`, error);
      const classified = handleApiError(error, `Retry ${operation.type}`);
      setOperationStatus(prev => ({
        ...prev,
        [operation.id]: { status: 'failed', error: classified.userMessage }
      }));
    }
  };

  const createNewConversation = async () => {
    const operationId = `create-${Date.now()}`;
    
    setOperationStatus(prev => ({
      ...prev,
      [operationId]: { status: 'loading', error: null }
    }));

    const operation = {
      id: operationId,
      type: 'create',
      fn: async () => {
        // Create conversation on server
        const newConversation = await conversationAPI.createConversation('New Chat');
        
        // Track new conversation creation
        trackNewConversation();
        
        // Get current model name for the initial message
        const currentModelName = models.find(m => m.id === selectedModel)?.originalName || selectedModel;
        
        // Add initial system message to the conversation
        await messageAPI.addMessage(
          newConversation.conversationId,
          'system',
          `Using ${currentModelName}, change in the top right`,
          selectedModel
        );
        
        // Create local conversation object
        const localConversation = {
          id: newConversation.conversationId,
          title: newConversation.title,
          messages: [{
            role: 'system',
            content: `Using ${currentModelName}, change in the top right`,
            isModelChange: true,
            timestamp: new Date().toISOString()
          }],
          createdAt: newConversation.createdAt,
          updatedAt: newConversation.updatedAt,
          model: selectedModel
        };

        const updatedConversations = {
          ...conversations,
          [newConversation.conversationId]: localConversation
        };

        setConversations(updatedConversations);
        setCurrentConversationId(newConversation.conversationId);
        saveConversations(updatedConversations);
        
        // Close sidebar on mobile after creating new chat
        if (isMobile) {
          setSidebarOpen(false);
        }
      }
    };

    try {
      await operation.fn();
      setOperationStatus(prev => ({
        ...prev,
        [operationId]: { status: 'success', error: null }
      }));
    } catch (error) {
      console.error('Error creating new conversation:', error);
      setOperationStatus(prev => ({
        ...prev,
        [operationId]: { status: 'failed', error: error.message }
      }));
      
      // Add to retry queue
      setRetryQueue(prev => [...prev, operation]);
      
      // Fallback to local creation if server fails
      const newId = generateConversationId();
      const currentModelName = models.find(m => m.id === selectedModel)?.originalName || selectedModel;
      
      const initialMessage = {
        role: 'system',
        content: `Using ${currentModelName}, change in the top right`,
        isModelChange: true,
        timestamp: new Date().toISOString()
      };
      
      const newConversation = {
        id: newId,
        title: 'New Chat',
        messages: [initialMessage],
        createdAt: new Date().toISOString(),
        model: selectedModel
      };

      const updatedConversations = {
        ...conversations,
        [newId]: newConversation
      };

      setConversations(updatedConversations);
      setCurrentConversationId(newId);
      saveConversations(updatedConversations);
      
      if (isMobile) {
        setSidebarOpen(false);
      }
    }
  };

  const renameConversation = async (conversationId, newTitle) => {
    const operationId = `rename-${conversationId}-${Date.now()}`;
    
    setOperationStatus(prev => ({
      ...prev,
      [operationId]: { status: 'loading', error: null }
    }));

    const operation = {
      id: operationId,
      type: 'rename',
      conversationId,
      fn: async () => {
        // Update conversation on server
        const updatedConversation = await conversationAPI.updateConversation(conversationId, { title: newTitle });
        
        // Update local state
        const updatedConversations = {
          ...conversations,
          [conversationId]: {
            ...conversations[conversationId],
            title: updatedConversation.title,
            updatedAt: updatedConversation.updatedAt
          }
        };

        setConversations(updatedConversations);
        saveConversations(updatedConversations);
      }
    };

    try {
      await operation.fn();
      setOperationStatus(prev => ({
        ...prev,
        [operationId]: { status: 'success', error: null }
      }));
    } catch (error) {
      console.error('Error renaming conversation:', error);
      setOperationStatus(prev => ({
        ...prev,
        [operationId]: { status: 'failed', error: error.message }
      }));
      
      // Add to retry queue
      setRetryQueue(prev => [...prev, operation]);
      
      // Still update local state optimistically
      const updatedConversations = {
        ...conversations,
        [conversationId]: {
          ...conversations[conversationId],
          title: newTitle,
          updatedAt: new Date().toISOString()
        }
      };

      setConversations(updatedConversations);
      saveConversations(updatedConversations);
    }
  };

  const startEditingTitle = (conversationId, currentTitle) => {
    setEditingTitleId(conversationId);
    setEditingTitleValue(currentTitle);
  };

  const cancelEditingTitle = () => {
    setEditingTitleId(null);
    setEditingTitleValue('');
  };

  const saveEditingTitle = async () => {
    if (editingTitleId && editingTitleValue.trim()) {
      await renameConversation(editingTitleId, editingTitleValue.trim());
      setEditingTitleId(null);
      setEditingTitleValue('');
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEditingTitle();
    } else if (e.key === 'Escape') {
      cancelEditingTitle();
    }
  };

  const deleteConversation = async (conversationId) => {
    // Show confirmation dialog
    const conversationTitle = conversations[conversationId]?.title || 'this conversation';
    const confirmed = window.confirm(
      `Are you sure you want to delete "${conversationTitle}"?\n`
    );
    
    if (!confirmed) {
      return; // User cancelled deletion
    }

    const operationId = `delete-${conversationId}-${Date.now()}`;
    
    setOperationStatus(prev => ({
      ...prev,
      [operationId]: { status: 'loading', error: null }
    }));

    const operation = {
      id: operationId,
      type: 'delete',
      conversationId,
      fn: async () => {
        // Delete conversation on server
        await conversationAPI.deleteConversation(conversationId);
        
        // Track chat deletion
        trackDeletedChat();
        
        // Update local state
        const updatedConversations = { ...conversations };
        delete updatedConversations[conversationId];

        setConversations(updatedConversations);
        saveConversations(updatedConversations);

        // If we deleted the current conversation, switch to another or create new
        if (conversationId === currentConversationId) {
          const remainingIds = Object.keys(updatedConversations);
          if (remainingIds.length > 0) {
            setCurrentConversationId(remainingIds[0]);
          } else {
            setCurrentConversationId(null);
          }
        }
      }
    };

    try {
      await operation.fn();
      setOperationStatus(prev => ({
        ...prev,
        [operationId]: { status: 'success', error: null }
      }));
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setOperationStatus(prev => ({
        ...prev,
        [operationId]: { status: 'failed', error: error.message }
      }));
      
      // Add to retry queue
      setRetryQueue(prev => [...prev, operation]);
      
      // Still update local state even if server delete fails
      const updatedConversations = { ...conversations };
      delete updatedConversations[conversationId];

      setConversations(updatedConversations);
      saveConversations(updatedConversations);

      if (conversationId === currentConversationId) {
        const remainingIds = Object.keys(updatedConversations);
        if (remainingIds.length > 0) {
          setCurrentConversationId(remainingIds[0]);
        } else {
          setCurrentConversationId(null);
        }
      }
    }
  };

  // Load more messages for pagination
  const loadMoreMessages = async (conversationId) => {
    if (isLoadingMessages) return;
    
    const currentPage = messagePages[conversationId];
    if (currentPage && !currentPage.hasMore) return;
    
    setIsLoadingMessages(true);
    
    try {
      const result = await messageAPI.getConversationMessages(
        conversationId,
        50,
        currentPage?.oldestTimestamp
      );
      
      // Process messages to detect model change messages
      const processedMessages = processMessages(result.messages);

      setMessagePages(prev => ({
        ...prev,
        [conversationId]: {
          messages: [
            ...processedMessages,
            ...(prev[conversationId]?.messages || [])
          ],
          hasMore: result.hasMore,
          oldestTimestamp: result.oldestTimestamp
        }
      }));
    } catch (error) {
      console.error('Error loading more messages:', error);
      setConversationError(error.message);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const switchConversation = async (conversationId) => {
    setCurrentConversationId(conversationId);
    
    // Switch to the model used in this conversation
    const conversation = conversations[conversationId];
    if (conversation && conversation.model) {
      // Validate that the conversation's model still exists
      const validatedModel = validateAndSwitchModel(conversation.model, models, recommendedModel);
      setSelectedModel(validatedModel);
      
      // Update conversation if model was changed
      if (validatedModel !== conversation.model) {
        const updatedConversations = {
          ...conversations,
          [conversationId]: {
            ...conversation,
            model: validatedModel
          }
        };
        setConversations(updatedConversations);
        saveConversations(updatedConversations);
      }
    } else {
      // Fallback to default model if conversation doesn't have a model set
      const validatedModel = validateAndSwitchModel(defaultModel, models, recommendedModel);
      setSelectedModel(validatedModel);
    }
    
    // Load messages for this conversation if not already loaded
    if (!messagePages[conversationId] && conversation && (!conversation.messages || conversation.messages.length === 0)) {
      setIsLoadingMessages(true);
      try {
        const result = await messageAPI.getConversationMessages(conversationId);
        
        // Process messages to detect model change messages
        const processedMessages = processMessages(result.messages);

        setMessagePages(prev => ({
          ...prev,
          [conversationId]: {
            messages: processedMessages,
            hasMore: result.hasMore,
            oldestTimestamp: result.oldestTimestamp
          }
        }));
      } catch (error) {
        console.error('Error loading messages for conversation:', error);
        
        // Check if this is a 404 error (conversation not found/deleted)
        if (error.message.includes('404') || error.message.includes('not found') || error.message.includes('access denied')) {
          console.log('🗑️ Conversation appears to be deleted, removing from local state and refreshing...');
          
          // Remove the conversation from local state
          setConversations(prev => {
            const updated = { ...prev };
            delete updated[conversationId];
            return updated;
          });
          
          // Clear current conversation if it's the deleted one
          if (currentConversationId === conversationId) {
            setCurrentConversationId(null);
          }
          
          // Refresh conversations from server to get updated list
          setTimeout(() => {
            loadConversations();
          }, 500);
          
          return; // Don't set error message for deleted conversations
        }
        
        setConversationError(error.message);
        
        // Fallback to local messages if available
        if (conversation.messages) {
          // Process messages to detect model change messages
          const processedMessages = processMessages(conversation.messages);

          setMessagePages(prev => ({
            ...prev,
            [conversationId]: {
              messages: processedMessages,
              hasMore: false,
              oldestTimestamp: null
            }
          }));
        }
      } finally {
        setIsLoadingMessages(false);
      }
    }
    
    // Close sidebar on mobile after switching conversation
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // API-based conversation functions
  const saveConversations = (convs) => {
    // Individual conversation updates are handled by API calls
    // This function is kept for compatibility but no longer saves to localStorage
  };

  const loadConversations = async () => {
    console.log('🔄 Loading conversations from server...');
    
    if (!isAuthenticated) {
      console.log('❌ Not authenticated, skipping conversation load');
      return;
    }

    setIsLoadingConversations(true);
    setConversationError(null);
    setCurrentError(null);

    try {
      const result = await conversationAPI.getConversations();
      console.log('📋 Fetched conversations:', result.conversations);
      
      // Check if result is from fallback
      if (result.isFromFallback) {
        setIsOfflineMode(true);
        setFallbackDataAge(result.fallbackAge);
        setCurrentError({
          message: result.fallbackReason || 'Using offline data',
          isFromFallback: true
        });
      } else {
        setIsOfflineMode(false);
        setFallbackDataAge(null);
      }
      
      if (result.conversations && result.conversations.length > 0) {
        // Convert server format to local format
        const formattedConversations = {};
        result.conversations.forEach(conversation => {
          formattedConversations[conversation.conversationId] = {
            id: conversation.conversationId,
            title: conversation.title,
            messages: [], // Messages will be loaded separately
            createdAt: conversation.createdAt,
            updatedAt: conversation.updatedAt,
            model: conversation.lastModel
          };
        });
        console.log('✅ Setting conversations state:', formattedConversations);
        setConversations(formattedConversations);

        // Set the most recent conversation as current and load its messages
        const mostRecent = result.conversations.reduce((latest, current) => {
          const currentDate = new Date(current.updatedAt || current.createdAt);
          const latestDate = new Date(latest.updatedAt || latest.createdAt);
          return currentDate > latestDate ? current : latest;
        });
        
        console.log('🎯 Setting current conversation to:', mostRecent.conversationId);
        setCurrentConversationId(mostRecent.conversationId);
        
        // Load messages for the most recent conversation
        try {
          const messagesResult = await messageAPI.getConversationMessages(mostRecent.conversationId);
          
          // Process messages to detect model change messages
          const processedMessages = processMessages(messagesResult.messages);

          setMessagePages(prev => ({
            ...prev,
            [mostRecent.conversationId]: {
              messages: processedMessages,
              hasMore: messagesResult.hasMore,
              oldestTimestamp: messagesResult.oldestTimestamp
            }
          }));
          
          // Set the model from the conversation (only if models are loaded)
          if (mostRecent.lastModel && models.length > 0) {
            const validatedModel = validateAndSwitchModel(mostRecent.lastModel, models, recommendedModel);
            setSelectedModel(validatedModel);
          } else if (mostRecent.lastModel) {
            // If models aren't loaded yet, just set the model directly
            setSelectedModel(mostRecent.lastModel);
          }
        } catch (error) {
          console.error('Error loading messages for most recent conversation:', error);
          // Don't fail the whole conversation loading if message loading fails
        }
      } else {
        console.log('📭 No conversations found');
        setConversations({});
        setCurrentConversationId(null);
      }
    } catch (error) {
      console.error('❌ Error loading conversations:', error);
      
      const classified = handleApiError(error, 'loadConversations');
      const recoveryStrategy = getRecoveryStrategy(error);
      
      // Handle authentication errors
      if (recoveryStrategy === RECOVERY_STRATEGIES.REAUTH) {
        logout();
        return;
      }
      
      // Set error for user notification
      setCurrentError(error);
      setConversationError(classified.userMessage);
      
      // No localStorage fallback - users must be authenticated
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Simple ConnectionStatus component
  const ConnectionStatus = () => {
    if (isOfflineMode) {
      return <div className="connection-status offline">📱 Offline</div>;
    }
    return null;
  };

  // Simple ServiceRecoveryPanel component
  const ServiceRecoveryPanel = ({ onResetCircuitBreakers, onClearFallback }) => {
    if (!currentError && !isOfflineMode) return null;
    
    return (
      <div className="service-recovery-panel">
        <button onClick={onResetCircuitBreakers} className="recovery-button">
          🔄 Reset Connection
        </button>
        {fallbackDataAge && (
          <button onClick={onClearFallback} className="recovery-button">
            🗑️ Clear Offline Data
          </button>
        )}
      </div>
    );
  };

  return (
    <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
      <div className="App">
        <div className={`app-layout ${isMobile && sidebarOpen ? 'mobile-sidebar-open' : ''}`}>
          {/* Sidebar */}
          <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
            <div className="sidebar-header">
              <button 
                onClick={createNewConversation} 
                className={`new-chat-button ${Object.values(operationStatus).some(status => status.status === 'loading' && status.type === 'create') ? 'operation-loading' : ''}`}
                disabled={Object.values(operationStatus).some(status => status.status === 'loading')}
              >
                {Object.values(operationStatus).some(status => status.status === 'loading' && status.type === 'create') 
                  ? (sidebarOpen ? '⏳ Creating...' : '⏳') 
                  : (sidebarOpen ? '➕ New Chat' : '➕')
                }
              </button>
              <button
                onClick={() => {
                  const newSidebarState = !sidebarOpen;
                  setSidebarOpen(newSidebarState);
                  // Track sidebar toggle
                  if (newSidebarState) {
                    trackSidebarOpen();
                  } else {
                    trackSidebarClose();
                  }
                }}
                className="sidebar-toggle"
                title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {sidebarOpen ? '◀' : '▶'}
              </button>
            </div>

            {sidebarOpen && (
              <div className={`conversations-list ${isLoadingConversations ? 'loading' : ''}`}>
                {Object.values(conversations)
                  .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
                  .map(conversation => {
                    const deleteOperation = Object.entries(operationStatus).find(
                      ([id, status]) => id.includes(`delete-${conversation.id}`)
                    );
                    const renameOperation = Object.entries(operationStatus).find(
                      ([id, status]) => id.includes(`rename-${conversation.id}`)
                    );
                    const isDeleting = deleteOperation && deleteOperation[1].status === 'loading';
                    const deleteFailed = deleteOperation && deleteOperation[1].status === 'failed';
                    const isRenaming = renameOperation && renameOperation[1].status === 'loading';
                    const isEditing = editingTitleId === conversation.id;
                    
                    return (
                      <div
                        key={conversation.id}
                        className={`conversation-item ${conversation.id === currentConversationId ? 'active' : ''} ${isDeleting ? 'operation-loading' : ''} ${deleteFailed ? 'operation-failed' : ''} ${isEditing ? 'editing' : ''}`}
                        onClick={() => !isEditing && switchConversation(conversation.id)}
                      >
                        {isEditing ? (
                          <div className="conversation-title-edit">
                            <input
                              type="text"
                              value={editingTitleValue}
                              onChange={(e) => setEditingTitleValue(e.target.value)}
                              onKeyDown={handleTitleKeyDown}
                              onBlur={saveEditingTitle}
                              autoFocus
                              maxLength={100}
                              className="title-input"
                            />
                          </div>
                        ) : (
                          <div 
                            className="conversation-title"
                            onDoubleClick={() => startEditingTitle(conversation.id, conversation.title)}
                            title="Double-click to rename"
                          >
                            {conversation.title}
                            {isRenaming && <span className="rename-indicator"> ⏳</span>}
                          </div>
                        )}
                        <div className="conversation-actions">
                          {!isEditing && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingTitle(conversation.id, conversation.title);
                              }}
                              className="rename-conversation"
                              title="Rename conversation"
                              disabled={isDeleting || isRenaming}
                            >
                              ✏️
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isEditing) {
                                cancelEditingTitle();
                              } else {
                                deleteConversation(conversation.id);
                              }
                            }}
                            className={`delete-conversation ${isDeleting ? 'operation-loading' : ''}`}
                            disabled={isDeleting}
                            title={isEditing ? "Cancel editing" : "Delete conversation"}
                          >
                            {isEditing ? '✖️' : (isDeleting ? '⏳' : '🗑️')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Main Chat Area */}
          <div className="main-content">
            <header className="app-header">
              <h1>🎓 Practicum</h1>
              <div className="header-controls">
                <ConnectionStatus />
                <div className="model-selector">
                  <label>Model:</label>
                  <ModelSelector />
                </div>
                <div className="user-info">
                  {currentUser && (
                    <>
                      <span className="user-email">👤 {currentUser.email}</span>
                      {currentUser.role === 'admin' && (
                        <a href="/admin/dashboard" className="admin-link">
                          🛠️ Admin
                        </a>
                      )}
                    </>
                  )}
                  <button onClick={logout} className="logout-button">
                    🚪 Logout
                  </button>
                </div>
              </div>
            </header>

            <main className="chat-container">
              <div className="messages">
                {isOfflineMode && (
                  <div className="fallback-mode-banner">
                    <span className="fallback-icon">📱</span>
                    Working in offline mode. Changes will sync when connection is restored.
                    {fallbackDataAge && (
                      <span> (Data from {Math.floor(fallbackDataAge / 60000)} minutes ago)</span>
                    )}
                  </div>
                )}

                {currentError && (
                  <ErrorNotification
                    error={currentError}
                    onDismiss={() => setCurrentError(null)}
                    onRetry={retryCurrentError}
                    onFallback={enableOfflineMode}
                    onReauth={logout}
                    showDetails={true}
                  />
                )}

                <ServiceRecoveryPanel
                  onResetCircuitBreakers={resetCircuitBreakers}
                  onClearFallback={clearFallbackData}
                />

                {conversationError && !currentError && (
                  <div className="error-banner">
                    ⚠️ {conversationError}
                    <button onClick={() => setConversationError(null)} className="dismiss-error">×</button>
                  </div>
                )}

                {/* Retry Panel for Failed Operations */}
                {retryQueue.length > 0 && (
                  <div className="retry-panel">
                    <div className="retry-header">
                      ⚠️ {retryQueue.length} operation{retryQueue.length > 1 ? 's' : ''} failed
                    </div>
                    {retryQueue.map(operation => (
                      <div key={operation.id} className="retry-item">
                        <span className="retry-description">
                          {operation.type === 'create' ? 'Create conversation' : 
                           operation.type === 'delete' ? 'Delete conversation' : 
                           'Operation'} failed
                        </span>
                        <div className="retry-actions">
                          <button 
                            onClick={() => retryOperation(operation)}
                            disabled={operationStatus[operation.id]?.status === 'retrying'}
                            className="retry-button"
                          >
                            {operationStatus[operation.id]?.status === 'retrying' ? '⏳ Retrying...' : '🔄 Retry'}
                          </button>
                          <button 
                            onClick={() => setRetryQueue(prev => prev.filter(op => op.id !== operation.id))}
                            className="dismiss-retry"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isLoadingConversations && (
                  <div className="loading-banner">
                    🔄 Loading conversations...
                  </div>
                )}

                {/* Load More Messages Button */}
                {currentConversationId && messagePages[currentConversationId]?.hasMore && (
                  <div className="load-more-container">
                    <button 
                      onClick={() => loadMoreMessages(currentConversationId)}
                      disabled={isLoadingMessages}
                      className="load-more-button"
                    >
                      {isLoadingMessages ? '⏳ Loading...' : '📜 Load Earlier Messages'}
                    </button>
                  </div>
                )}

                {messages.length === 0 && !isLoadingConversations && (
                  <div className="welcome-message">
                    <h2>Welcome to Practicum!</h2>  
                    <p>Chat with multiple AI models — all in one place.</p>
                    <p>Swap between Claude, Llama, Nova and others anytime to see different perspectives.</p>
                    <p>Start a new chat or pick up where you left off to explore ideas your way.</p>
                  </div>
                )}

                {messages.map((message, index) => (
                  <div key={index} className={`message ${message.role} ${message.isError ? 'error' : ''} ${message.isModelChange ? 'model-change' : ''} ${message.isOptimistic ? 'optimistic' : ''} ${message.isTruncated ? 'truncated' : ''}`}>
                    <div className="message-content">
                      {message.content}
                    </div>
                    {message.isOptimistic && (
                      <div className="message-status">Sending...</div>
                    )}
                    {message.isTruncated && message.role === 'assistant' && currentConversationId && (
                      <div className="message-actions">
                        <button 
                          onClick={() => continueResponse(currentConversationId)}
                          disabled={isLoading}
                          className="continue-button"
                          title="Continue this response"
                        >
                          {isLoading ? '⏳ Continuing...' : '➡️ Continue'}
                        </button>
                        <span className="truncation-notice">Response was truncated due to length limits</span>
                      </div>
                    )}
                    {message.isError && message.adminDetails && currentUser?.role === 'admin' && (
                      <details className="admin-error-details">
                        <summary>🔧 Admin Error Details</summary>
                        <pre className="admin-error-content">
                          {JSON.stringify(message.adminDetails, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="message assistant loading">
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="input-container">
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => {
                    // Scroll the textarea into view when keyboard appears on mobile
                    if (isMobile) {
                      // Multiple attempts to ensure visibility
                      const scrollIntoViewMultiple = () => {
                        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      };
                      
                      setTimeout(scrollIntoViewMultiple, 100);
                      setTimeout(scrollIntoViewMultiple, 300);
                      setTimeout(scrollIntoViewMultiple, 500);
                    }
                  }}
                  placeholder="Type your message here..."
                  disabled={isLoading}
                  rows={3}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="send-button"
                >
                  {isLoading ? '⏳' : '📤'}
                </button>
              </div>
            </main>

            <footer className="app-footer">
              Built with ❤️ by <a href="https://jackbritchford.com" target="_blank" rel="noopener noreferrer">Jack Britchford</a> for the love of my life
            </footer>
          </div>

          {/* Mobile floating sidebar toggle button */}
          {isMobile && !sidebarOpen && (
            <button
              onClick={() => {
                setSidebarOpen(true);
                trackSidebarOpen();
              }}
              className="mobile-sidebar-toggle"
              title="Open sidebar"
            >
              ☰
            </button>
          )}
        </div>
        
        <AddToHomeScreenPrompt conversations={conversations} />
      </div>
    </ErrorBoundary>
  );
}

export default ChatApp;